import React, { useEffect, useState } from 'react';
import { reportingDataService } from '../../../reports/services';
import { InstructionWrapper, DatePicker } from '../../../../components/fields';
import { useStateReducer } from '../../../../hooks';
import './_styles.scss';
import securityService from '../../../../_global/services/securityService';
import moment from 'moment';
import config from '../../../../_config/global';
import { Button, Form } from '../../../../components';
import Heading from '../../../../_global/components/heading/Heading';
import LoadingBar from '../../../../components/loading-bar/LoadingBar';
import { Regexes, UserPermissions } from '../../../../definitions';
import { hideAlert, showAlert } from '../../../../components/alert/Alert';
import { AlertTypeEnum } from '../../../../enums';
import { Divider, MenuItem, Select } from '@mui/material';
import { formatDate } from '../../../../utilities/stringUtils';
import { utils, writeFile } from 'xlsx';
import dataService from '../../services/dataService';
import { EListOfPlans } from '../../../../enums';
import {
  Data,
  PartnerShareAllResponse,
  SucceededResponse as succ
} from './../../../../types/ReportTypes';
import validateObject from '../../../../utilities/validateObject';
import InfiniteList from '../../../../components/fields/InfiniteList';

interface PartnerShareReport {
  data: any;
}

export interface ReportData {
  partial_refund: PartialRefunds[];
  succeeded: Succeeded[];
  failed: any[];
  refunded: Refunded[];
  total_amount_succeeded: number;
  total_amount_failed: number;
  total_amount_refunded: number;
  net_amount_succeeded: number;
  partner_share: string;
  partner_agreed_share: string;
}

export interface PendingUrls {
  link: string;
  _calculated_to_pay: number;
  metadata: Metadata;
}

export interface Metadata {
  fromDate: string;
  toDate: string;
  influencer_link_code: string;
  email: string;
}

export interface PaymentLink {
  url: string;
  amount: number;
}
export interface Payouts {
  total: number;
}

export interface PartialRefunds {
  payment_status: string;
  amounts: number;
  total_excluding_tax: number;
  amount_off: any;
  percent_off: number;
  amount_refunded: number;
  amount_captured: number;
  receipt_email: string;
  currency: string;
  created_at: string;
  payment_method: string;
  charge_id: string;
  invoice_id: string;
  description: string;
  legacy_source: boolean;
  _total_calculated_spent_after_tax_deducted_partial_refunds: number;
}

export interface Succeeded {
  payment_status: string;
  amounts: number;
  total_excluding_tax: number;
  amount_off: any;
  percent_off: number;
  amount_refunded: number;
  amount_captured: number;
  receipt_email: string;
  currency: string;
  created_at: string;
  payment_method: string;
  charge_id: string;
  invoice_id: string;
  description: string;
  legacy_source: boolean;
  dots_payment: boolean;
  _total_calculated_spent_after_tax_deducted_partial_refunds: number;
  _partner_share: number;
}

export interface Refunded {
  payment_status: string;
  amounts: number;
  total_excluding_tax: number;
  amount_off: any;
  percent_off: number;
  amount_refunded: number;
  amount_captured: number;
  receipt_email: string;
  currency: string;
  created_at: string;
  payment_method: string;
  charge_id: string;
  invoice_id: string;
  description: string;
  legacy_source: boolean;
  _total_calculated_spent_after_tax_deducted_partial_refunds: number;
}

interface UserConversionModal {
  _id: string;
  total_count: number;
  active: Active;
  inactive: string[];
}

interface Active {
  manual: any[];
  platinum: string[];
  premium: any[];
  gold: any[];
}

export default function PartnerShareByCode({ data }: PartnerShareReport) {
  const [activeListIndex, setActiveListIndex] = useState(0);
  const _SHOW_EMAIL_AMOUNT = 5;
  const [state, setState] = useStateReducer({
    ...data,
    reportData: {} as ReportData,
    linkCode: '',
    fromDate: moment(new Date(), config.dateFormat).subtract(30, 'days'),
    toDate: moment(new Date(), config.dateFormat),
    selectedCode: '',
    UserConversionData: [] as UserConversionModal[],
    isLoading: false,
    showEmailsAmount: _SHOW_EMAIL_AMOUNT,
    selectedOptions: [],
    exportDateFrom: moment(new Date(), config.dateFormat).subtract(30, 'days'),
    exportDateTo: moment(new Date(), config.dateFormat),
    isAdmin: false
  });

  const listOfPlans = [
    {
      title: EListOfPlans.Platinum,
      data: state.userConversionData?.active?.platinum || []
    },
    {
      title: EListOfPlans.Premium,
      data: state.userConversionData?.active?.premium || []
    },
    {
      title: EListOfPlans.Gold,
      data: state.userConversionData?.active?.gold || []
    },
    {
      title: EListOfPlans.Free,
      data: state.userConversionData?.inactive || []
    },
    {
      title: EListOfPlans.Trial,
      data: state.userConversionData?.active?.trial || []
    },
    {
      title: EListOfPlans.Trip_Pass,
      data: state.userConversionData?.active?.tripPass || []
    }
  ].filter((list, index) => list?.data?.length > 0);

  const fetchPaymentReportData = async (
    fromDate: moment.Moment,
    toDate: moment.Moment,
    selectedCode?: string
  ) => {
    try {
      //start loading and set the date range
      setState({ isLoading: true, fromDate, toDate });

      // Fetch the report data
      const reportData: ReportData =
        await reportingDataService.getPartnerShareByCode({
          email: securityService.getCurrentUserEmail(),
          influencer_link_code: selectedCode ?? state.selectedCode,
          fromDate: fromDate,
          toDate: toDate
        });

      const selectedOptions: string[] = [];
      if (!Array.isArray(reportData.succeeded))
        return setState({ reportData: {} as ReportData, isLoading: false });

      // Get the selected options from the report data
      reportData?.succeeded?.forEach((element) => {
        if (
          state.selectedOptions.indexOf(element.invoice_id) === -1 &&
          element.dots_payment === false
        )
          selectedOptions.push(element.invoice_id);
      });

      // Update the state with the fetched data and stop loading
      setState({
        reportData: reportData,
        isLoading: false,
        activeListIndex: 0,
        selectedOptions: selectedOptions
      });
    } catch (e) {
      // Show an error alert if fetching the data fails
      setState({isLoading: false})
      if (e instanceof Error){
        showAlert({
          content: e.message
            ? e.message
            : 'There was an error fetching the Revenue Share Payment data, please try again.',
          options: {
            actions: [
              {
                text: 'Close',
                onClick: () => {
                  hideAlert();
                }
              }
            ],
            type: AlertTypeEnum.Error
          }
        });
      }
    }
  };

  const fetchPaymentLink = async (
    generateLink?: Boolean,
    paymentsToExclude?: []
  ) => {
    setState({ isLoadingPartner: true });
    var paymentLink: PaymentLink =
      await reportingDataService.getPaymentLinkByCode({
        email: securityService.getCurrentUserEmail(),
        influencer_link_code: state.selectedCode,
        fromDate: state.fromDate,
        toDate: state.toDate,
        paymentsToExclude: paymentsToExclude ?? [],
        generateLink: generateLink ?? false
      });

    setState({
      paymentLink: paymentLink,
      isLoadingPartner: false
    });
  };

  const fetchPendingPartnerUrls = async () => {
    const pendingUrls: PendingUrls =
      await reportingDataService.getPendingPartnerUrls(state.selectedCode);

    setState({
      pendingUrls: pendingUrls
    });
  };

  const fetchTotalPayout = async () => {
    try {
      setState({ isLoading: true });
      const payouts: Payouts = await reportingDataService.getTotalPayoutByCode(
        state.selectedCode
      );

      setState({ payouts: payouts, isLoading: false });
    } catch (e) {
      if (e instanceof Error)
        showAlert({
          content: e.message
            ? e.message
            : 'There was an error fetching Total Payout, please try again.',
          options: {
            actions: [
              {
                text: 'Close',
                onClick: () => {
                  hideAlert();
                }
              }
            ],
            type: AlertTypeEnum.Error
          }
        });
    }
  };

  const fetchUserConversionData = async (partnerCode: string) => {
    try {
      const userConversionData: UserConversionModal[] =
        await reportingDataService.getUserConversionsByCode(partnerCode);

      setState({
        userConversionData: userConversionData ?? []
      });
    } catch (e) {
      if (e instanceof Error)
        showAlert({
          content:
            e.message ||
            'There was an error fetching User Conversion data, please try again.',
          options: {
            actions: [
              {
                text: 'Close',
                onClick: () => {
                  hideAlert();
                }
              }
            ],
            type: AlertTypeEnum.Error
          }
        });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps

  const getRevenueByPartnerCode = async (selectedCode: string) => {
    try {
      if (selectedCode === '') throw new Error('Partner Code is required');
      setState({ selectedCode });

      await Promise.all([
        fetchPaymentReportData(state.fromDate, state.toDate, selectedCode),
        fetchUserConversionData(selectedCode)
      ]);
    } catch (e) {
      if (e instanceof Error)
        showAlert({
          content: e.message
            ? e.message
            : 'There was an error fetching the Partner Code data, please try again.',
          options: {
            actions: [
              {
                text: 'Close',
                onClick: () => {
                  setState({ id: '', isOpened: false });
                  hideAlert();
                }
              }
            ],
            type: AlertTypeEnum.Info
          }
        });
    }
  };

  useEffect(() => {
    if (!state.selectedCode) return;
    if (securityService.checkRoleAccess(UserPermissions.isSuperUser)) {
      Promise.allSettled([fetchTotalPayout(), fetchPendingPartnerUrls()]);
    }
  }, [state.selectedCode, state.fromDate, state.toDate]);

  useEffect(() => {
    if (securityService.checkRoleAccess(UserPermissions.isSuperUser)) {
      setState({isAdmin: true})
      fetchPendingPartnerUrls();
    }
  }, []);

  function paginationEmails() {
    // less than default
    if (listOfPlans[activeListIndex].data.length < _SHOW_EMAIL_AMOUNT)
      return <div></div>;

    if (listOfPlans[activeListIndex].data.length > state.showEmailsAmount)
      return (
        <div onClick={() => setState({ showEmailsAmount: 1000 })}>
          ..... +{listOfPlans[activeListIndex].data.length - _SHOW_EMAIL_AMOUNT}{' '}
          more
        </div>
      );

    return (
      <div onClick={() => setState({ showEmailsAmount: _SHOW_EMAIL_AMOUNT })}>
        Show Less
      </div>
    );
  }

  function ShowConversionEmails() {
    if (state.userConversionData?.active?.manual?.length < 1) return;

    let message = (
      <>
        {
          <div>
            {state.userConversionData?.active?.manual &&
              state.userConversionData.active.manual.map(
                (obj: any, index: number) => (
                  <div key={index}>{obj.email_address}</div>
                )
              )}
          </div>
        }
        {}
      </>
    );

    setState({ isOpened: true });
    showAlert({
      content: message,
      options: {
        actions: [
          {
            text: 'Close',
            onClick: () => {
              setState({ id: '', isOpened: false });
              hideAlert();
            }
          }
        ],
        type: AlertTypeEnum.Info
      }
    });
  }

  function ShowDetails(arg0: number) {
    if (!state.reportData.succeeded) return;
    let selectedList = state.reportData?.failed;
    let title = 'Failed';

    if (arg0 === 1) {
      selectedList = [...state.reportData?.succeeded];

      title = 'Received';
    } else if (arg0 === 2) {
      selectedList = state.reportData?.refunded;
      title = 'Refunded';
    } else if (arg0 === 3) {
      title = 'Partner Share';
    } else if (arg0 === 4) {
      selectedList = state.reportData?.failed;
    }

    let message;
    if (arg0 === 3) {
      message = (
        <>
          {
            <div>
              <h2>{title}</h2>
              CALCULATION:
              <div className='spacing'></div>
              The total amount, which is the money received after deducting
              taxes amounts to
              <b>
                {' '}
                ${state.reportData?.net_amount_succeeded?.toFixed(2) ?? '0'}
              </b>
              .<div className='spacing'></div>
              The net total is
              <b>
                {' '}
                $
                {(state.reportData?.net_amount_succeeded * 0.97).toFixed(2) ??
                  '0'}{' '}
              </b>
              after Stripe’s 3% fee is subtracted.
              <div className='spacing'></div>
              This is then multiplied by the agreed upon revenue share
              percentage (30%) thus, the partner's revenue share comes to $
              <b>{state.reportData?.partner_share ?? '0'}.</b>
              <div className='spacing'></div>
            </div>
          }
          {}
        </>
      );
    } else {
      message = (
        <>
          {
            <div>
              <h2>{title}</h2>
              {selectedList.map((c: any) => (
                <div>
                  <label>
                    {arg0 === 2 ? 'Refunded Amount' : 'Payment Amount'} :
                  </label>
                  <label>
                    <b>
                      {' '}
                      {arg0 === 2
                        ? c.amount_refunded
                        : c._total_calculated_spent_after_tax_deducted_partial_refunds ??
                          '-'}
                    </b>
                  </label>
                  <br />
                  {c.amount_off != null ? (
                    <>
                      <label>Amount Off :</label>
                      <label>
                        <b> {c.amount_off}</b>
                      </label>
                      <br />
                    </>
                  ) : c.percent_off != null ? (
                    <>
                      <label>Percentage Off :</label>
                      <label>
                        <b> {c.percent_off}</b>
                      </label>
                      <br />
                    </>
                  ) : null}
                  <label>Payment Date :</label>
                  <label>
                    <b> {c.created_at}</b>
                  </label>
                  <br />
                  <label>Payment Description :</label>
                  <label>
                    <b> {c.description}</b>
                  </label>
                  <br />
                  <label>Receipt Email :</label>
                  <label>
                    <b> {c.receipt_email}</b>
                  </label>
                  <br />
                  {securityService.checkRoleAccess(
                    UserPermissions.isSuperUser
                  ) && (
                    <>
                      <label>Legacy Source :</label>
                      <label>
                        <b>{c.legacy_source ? ' True' : ' False'}</b>
                      </label>
                      <br />
                      <label>Already Paid :</label>
                      <label>
                        <b>{c.dots_payment ? ' True' : ' False'}</b>
                      </label>
                      <br />
                    </>
                  )}
                  <br />
                </div>
              ))}
            </div>
          }
          {}
        </>
      );
    }

    setState({ isOpened: true });
    showAlert({
      content: message,
      options: {
        actions: [
          {
            text: 'Close',
            onClick: () => {
              setState({ id: '', isOpened: false });
              hideAlert();
            }
          }
        ],
        type: AlertTypeEnum.Info
      }
    });
  }

  function showPendingUrls() {
    async function handleClick(e: any) {
      const value = e.currentTarget.value;
      if (value) {
        navigator.clipboard
          .writeText(value)
          .then(() => {
            console.log('Text copied to clipboard:', value);
          })
          .catch((error) => {
            console.error('Failed to copy text to clipboard:', error);
          });
      }
    }

    let message = (
      <>
        <div className='generated-urls-container'>
          <h2>Generated Urls</h2>
          <div className='table'>
            <div className='table-row'>
              <div className='table-cell'>Link</div>
              <div className='table-cell'>Calculated to Pay</div>
              <div className='table-cell'>From Date</div>
              <div className='table-cell'>To Date</div>
              <div className='table-cell'>Actions</div>
            </div>
            {state?.pendingUrls?.map((c: PendingUrls) => (
              <div className='table-row'>
                <div className='table-cell'>{`${c.link.substring(
                  c.link.length - 20,
                  c.link.length
                )}...`}</div>
                <div className='table-cell'>${c._calculated_to_pay}</div>
                <div className='table-cell'>
                  {formatDate(c.metadata.fromDate, config.dateFormat)}
                </div>
                <div className='table-cell'>
                  {formatDate(c.metadata.toDate, config.dateFormat)}
                </div>
                <div className='table-cell'>
                  <Button
                    onClick={handleClick}
                    value={c.link}
                    raised
                    primary
                    text='Copy to Clipboard'
                  ></Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {}
      </>
    );

    setState({ isOpened: true });
    showAlert({
      content: message,
      options: {
        actions: [
          {
            text: 'Close',
            onClick: () => {
              setState({ id: '', isOpened: false });
              hideAlert();
            }
          }
        ],
        type: AlertTypeEnum.Info
      }
    });
  }

  async function handleClick(e: any) {
    e.preventDefault();
    let excludedList = state.reportData?.succeeded.filter(
      (obj: any) =>
        !state.selectedOptions.some((value: any) =>
          obj.invoice_id.includes(value)
        )
    );

    excludedList = excludedList.map((e: any) => e.charge_id);

    await fetchPaymentLink(true, excludedList);
    fetchPaymentReportData(state.fromDate, state.toDate);
    fetchPendingPartnerUrls();
  }

  async function handleExport() {
    try {
      if (!state.exportDateFrom && !state.exportDateTo)
        throw new Error('Please select a date range');

      const data: PartnerShareAllResponse =
        await reportingDataService.getAllPartnerRevenueShareByCode(
          state.exportDateFrom,
          state.exportDateTo
        );
      const parsedData: Data[] = [
        ...data.succeeded.map((doc: succ) => {
          return doc.data;
        })
      ];

      const promises = parsedData.map(async (data) => {
        const [userConversions, userPayouts, pendingUrls] = await Promise.all([
          dataService.getUserConversionsByCode(data.code),
          dataService.getTotalPayoutByCode(data.code),
          dataService.getPendingPartnerUrls(data.code)
        ]);
        return {
          userConversions,
          userPayouts,
          pendingUrls
        };
      });

      const results = await Promise.all(promises);
      const userConversionData = results.map((r) => r.userConversions);
      const userPayoutData = results.map((r) => r.userPayouts);
      const pendingUrls = results.map((r) => r.pendingUrls);

      const formatExportDateFrom = formatDate(
        state.exportDateFrom.toString(),
        config.dateFormat
      );
      const formatExportDateTo = formatDate(
        state.exportDateTo.toString(),
        config.dateFormat
      );
      const firstRowHeaders = [
        'Date Range',
        formatExportDateFrom,
        formatExportDateTo
      ];
      const secondRowHeaders = [' '];
      const thirdRowHeaders = [
        ' ',
        ' ',
        ' ',
        ' ',
        ' ',
        ' ',
        ' ',
        ' ',
        ' ',
        'Payouts',
        ' ',
        ' ',
        'All Conversions'
      ];
      const firstColumnHeaders = [
        'Partner Link',
        'No. of Payments',
        'No. of Refunds',
        'No. of Failures',
        'Total Amount Paid',
        ' Total Amount Refunded',
        'Total Amount Failed',
        'Partner Share',
        'Net Total'
      ];
      const secondColumnHeaders = [
        'Partner Payout for Dates Selected',
        'Total Partner Payout',
        'Pending Links'
      ];
      const thirdColumnHeaders = [
        EListOfPlans.Platinum,
        EListOfPlans.Premium,
        EListOfPlans.Gold,
        EListOfPlans.Free,
        EListOfPlans.Trip_Pass,
        'Manual Access',
        'Total',
        'Main Partner Label',
        'Secondary Partner Label'
      ];
      const parsedRowData = [];
      for (let i = 0; i < parsedData.length; i++) {
        // Loop through the succeeded array to calculate Parner Payout for selected Dates
        let payout: number = 0;
        if (parsedData[i].succeeded && parsedData[i].succeeded.length > 0)
          parsedData[i].succeeded.map((ob: any, index: number) => {
            payout += ob._partner_share;
          });

        const awayz_access = {
          platinum:
            userConversionData[i] && userConversionData[i].active
              ? userConversionData[i].active.platinum.length
              : 0,
          premium:
            userConversionData[i] && userConversionData[i].active
              ? userConversionData[i].active.premium.length
              : 0,
          gold:
            userConversionData[i] && userConversionData[i].active
              ? userConversionData[i].active.gold.length
              : 0,
          free:
            userConversionData[i] && userConversionData[i].inactive
              ? userConversionData[i].inactive.length
              : 0,
          trip_pass:
            userConversionData[i] && userConversionData[i].active
              ? userConversionData[i].active.tripPass.length
              : 0,
          manual_access:
            userConversionData[i] && userConversionData[i].active
              ? userConversionData[i].active.manual.length
              : 0,
          total:
            userConversionData[i] &&
            userConversionData[i].active &&
            userConversionData[i].inactive
              ? userConversionData[i].active.platinum.length +
                userConversionData[i].active.premium.length +
                userConversionData[i].active.gold.length +
                userConversionData[i].active.tripPass.length +
                userConversionData[i].active.manual.length +
                userConversionData[i].inactive.length
              : 0
        };

        const row = [
          parsedData[i].code,
          parsedData[i].count.succeeded_count,
          parsedData[i].count.refunded_count,
          parsedData[i].count.failed_count,
          parsedData[i].net_amount_succeeded
            ? parsedData[i].net_amount_succeeded
            : 0,
          parsedData[i].total_amount_refunded,
          parsedData[i].total_amount_failed,
          parsedData[i].partner_share ? parsedData[i].partner_share : 0,
          parsedData[i].net_amount_succeeded
            ? parsedData[i].net_amount_succeeded
            : 0,
          payout ? payout : 0,
          userPayoutData[i].total ? userPayoutData[i].total : 0,
          pendingUrls[i].length,
          awayz_access.platinum,
          awayz_access.premium,
          awayz_access.gold,
          awayz_access.free,
          awayz_access.trip_pass,
          awayz_access.manual_access,
          awayz_access.total,
          parsedData && parsedData[i] && parsedData[i].partner_category
            ? parsedData[i].partner_category!.primary
            : 'N/A',
          parsedData && parsedData[i].partner_category
            ? parsedData[i].partner_category!.secondary
            : 'N/A',
          parsedData[i].failReason ?? parsedData[i].failReason
        ];
        parsedRowData.push(row);
      }

      const dataHeaders = [
        ...firstColumnHeaders,
        ...secondColumnHeaders,
        ...thirdColumnHeaders
      ];
      const allHeaders = [
        firstRowHeaders,
        secondRowHeaders,
        thirdRowHeaders,
        dataHeaders,
        ...parsedRowData
      ];

      // Create a worksheet
      let ws = utils.aoa_to_sheet(allHeaders);

      const merges = [
        // Merge cells for 'Payouts' spanning 4 columns (columns 8 to 11).
        { s: { r: 2, c: 9 }, e: { r: 2, c: 11 } },

        // Merge cells for 'All Conversions' spanning 5 columns (columns 12 to 16).
        { s: { r: 2, c: 12 }, e: { r: 2, c: 18 } }
      ];

      // Set the merges property in the worksheet
      ws['!merges'] = merges;

      // Create a workbook with the worksheet
      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, 'Sheet1');
      writeFile(wb, 'PartnersRevenueShare.xlsx');
    } catch (e) {
      if (e instanceof Error)
        showAlert({
          content: e.message
            ? e.message
            : 'There was an error generating the report, please try again.',
          options: {
            actions: [
              {
                text: 'Close',
                onClick: () => {
                  hideAlert();
                }
              }
            ],
            type: AlertTypeEnum.Error
          }
        });
    }
  }

  const handleChangeSelect = (event: any) => {
    const { value } = event.target;
    setState({ selectedOptions: value });
  };

  function getAmount() {
    let payOutAmount = 0;
    state.selectedOptions.forEach((element: string) => {
      let filteredInvoice = state?.reportData?.succeeded?.filter(
        (data: any) => {
          return data.invoice_id === element;
        }
      );

      if (filteredInvoice?.length > 0)
        payOutAmount += filteredInvoice[0]._partner_share;
    });

    return payOutAmount.toFixed(2);
  }

  const errors = validateObject(
    {
      selectedCode: {
        regex: Regexes.anyContentRegex,
        message: 'Please select a primary category.'
      }
    },
    state
  );

  return (
    <div className='order-item'>
      {state.isAdmin && <div className='export-csv'>
        <div>
          <DatePicker
            label='From'
            className='date-selector-partner'
            value={state.exportDateFrom}
            onChange={(date: any) => {
              setState({ exportDateFrom: date });
            }}
          />
          <DatePicker
            label='To'
            className='date-selector-partner'
            minDate={state.exportDateFrom}
            value={state.exportDateTo}
            onChange={(date: any) => {
              setState({ exportDateTo: date });
            }}
          />
        </div>

        <Button
          className='category-option export-button'
          text='Export CSV'
          primary
          raised
          onClick={handleExport}
        ></Button>
      </div>}

      <LoadingBar
        isLoading={state.isLoading || state.isLoadingPartner}
      ></LoadingBar>
      <Heading name='Partner Revenue Share By Code'></Heading>

      <div className='row'>
        <InfiniteList
          error={errors['selectedCode'] ? true : false}
          label={'Partner Link'}
          onChange={(_, v) => {
            v?._id && getRevenueByPartnerCode(v?._id);
          }}
          onClear={() => {
            setState({ selectedCode: null });
          }}
          value={state.selectedCode}
          options={state.listCodes}
          getOptionLabel={(o) => o.label || state.selectedCode}
          getOptionValue={(o) => o.label || state.selectedCode}
          isOptionEqualToValue={(o, v) => o.label === v.label}
        />
        <DatePicker
          label='From'
          className='date-selector-partner'
          value={state.fromDate}
          onChange={(date: any) => {
            fetchPaymentReportData(date, state.toDate);
          }}
        />
        <DatePicker
          label='To'
          className='date-selector-partner'
          minDate={state.fromDate}
          value={state.toDate}
          onChange={(date: any) => {
            fetchPaymentReportData(state.fromDate, date);
          }}
        />
      </div>
      <br />

      <InstructionWrapper>
        <>
          <div
            className={`${
              securityService.checkRoleAccess(UserPermissions.isSuperUser)
                ? 'paragraphs'
                : ''
            } remove-padding`}
          >
            <div>
              <h2 className='categories-title' onClick={() => ShowDetails(1)}>
                Received
              </h2>
              <div onClick={() => ShowDetails(1)}>
                <Form className='category-option' title='No. of payments'>
                  {(state.reportData?.succeeded?.length ?? 0) +
                    (state.reportData?.partial_refund?.length || 0)}
                </Form>
                <Form className='category-option' title='Total amount'>
                  {(
                    Number(state.reportData?.net_amount_succeeded) || 0
                  ).toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD'
                  })}
                </Form>
              </div>
            </div>

            {securityService.checkRoleAccess(UserPermissions.isSuperUser) ? (
              <div>
                <h2 className='categories-title' onClick={() => ShowDetails(2)}>
                  Refunds
                </h2>
                <div onClick={() => ShowDetails(2)}>
                  <Form className='category-option' title='No. of refunds'>
                    {state.reportData?.refunded?.length ?? '0'}
                  </Form>
                  <Form className='category-option' title='Total amount'>
                    {(
                      Number(state.reportData?.total_amount_refunded) || 0
                    ).toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD'
                    })}
                  </Form>
                </div>
              </div>
            ) : null}

            {securityService.checkRoleAccess(UserPermissions.isSuperUser) ? (
              <div onClick={() => ShowDetails(4)}>
                <h2 className='categories-title'>Failures</h2>
                <div>
                  <Form className='category-option' title='No. of failures'>
                    {state.reportData?.failed?.length ?? '0'}
                  </Form>
                  <Form className='category-option' title='Total amount'>
                    {(
                      Number(state.reportData?.total_amount_failed) || 0
                    ).toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD'
                    })}
                  </Form>
                </div>
              </div>
            ) : null}
          </div>
          <Divider />
          <br />
          <div className='paragraphs'>
            <div onClick={() => ShowDetails(3)}>
              <Form className='category-option ' title='Partner share'>
                {(Number(state.reportData?.partner_share) || 0).toLocaleString(
                  'en-US',
                  {
                    style: 'currency',
                    currency: 'USD'
                  }
                )}
              </Form>
            </div>
            <div onClick={() => ShowDetails(3)}>
              <Form className='category-option' title='Net Total'>
                {(
                  Number(state.reportData?.net_amount_succeeded) * 0.97 || 0
                ).toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD'
                })}
              </Form>
            </div>
          </div>
          <br />
          {securityService.checkRoleAccess(UserPermissions.isSuperUser) && (
            <>
              <Heading name={'Payouts'}></Heading>
              <div className='paragraphs'>
                <div className='combined-payout'>
                  <Form
                    className='category-option '
                    title='Partner Payout for dates selected'
                  >
                    <div style={{ position: 'relative' }}>
                      <p>{`$ ${getAmount()}`}</p>
                      <p>
                        <Select
                          multiple
                          fullWidth
                          value={state.selectedOptions}
                          onChange={handleChangeSelect}
                          style={{
                            width: '200px',
                            maxWidth: '400px'
                          }}
                        >
                          {state?.reportData?.succeeded
                            ?.sort((elem: Succeeded) =>
                              elem.dots_payment ? 1 : 0
                            )
                            .map((obj: any, index: number) => {
                              let cleanedSubscription = obj.description;

                              if (
                                cleanedSubscription &&
                                cleanedSubscription.includes('(')
                              ) {
                                cleanedSubscription = cleanedSubscription
                                  .substring(
                                    0,
                                    cleanedSubscription.indexOf('(')
                                  )
                                  .trim();
                              }

                              if (
                                cleanedSubscription
                                  .toLowerCase()
                                  .includes('trip pass')
                              )
                                cleanedSubscription = `1 x ${cleanedSubscription}`;

                              return (
                                <MenuItem
                                  key={index}
                                  disabled={obj.dots_payment}
                                  value={`${obj.invoice_id}`}
                                >
                                  {`${cleanedSubscription} - ${obj._partner_share.toLocaleString(
                                    'en-US',
                                    {
                                      style: 'currency',
                                      currency: 'USD'
                                    }
                                  )}${obj.dots_payment ? ' - Paid' : ''}`}
                                </MenuItem>
                              );
                            })}
                        </Select>
                      </p>
                    </div>
                    <Button
                      className='category-option '
                      text='Generate Link'
                      primary
                      raised
                      onClick={handleClick}
                    ></Button>
                  </Form>
                </div>
                <div>
                  <div>
                    <Form
                      className='category-option'
                      title='Total Partner Payout'
                    >
                      {(Number(state.payouts?.total) || 0).toLocaleString(
                        'en-US',
                        {
                          style: 'currency',
                          currency: 'USD'
                        }
                      )}
                    </Form>
                  </div>
                  <div onClick={() => showPendingUrls()}>
                    <Form className='category-option' title='Pending Links'>
                      {`${state.pendingUrls?.length ?? 0} Links`}
                    </Form>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      </InstructionWrapper>
      <br />
      <Heading name={'All Conversions'}></Heading>
      <InstructionWrapper
        text={
          state.userConversionData?.total_count && (
            <>
              <div>
                <Heading name={listOfPlans[activeListIndex].title} />
                <div
                  style={{ display: 'flex', justifyContent: 'space-around' }}
                >
                  <div>
                    {listOfPlans[activeListIndex].data
                      .slice(0, state.showEmailsAmount)
                      .map((obj: any, index: number) => (
                        <li key={index}>{obj.email_address}</li>
                      ))}
                    {paginationEmails()}
                  </div>
                  <div>
                    {listOfPlans[activeListIndex].title !== 'Free'
                      ? listOfPlans[activeListIndex].data
                          .slice(0, state.showEmailsAmount)
                          .map((obj: any, index: number) => (
                            <li key={index}>
                              {obj.interval} {obj.is_trial ? '(trial)' : null}
                            </li>
                          ))
                      : null}
                    {paginationEmails()}
                  </div>
                </div>
              </div>
            </>
          )
        }
      >
        {state.userConversionData?.total_count ? (
          <div>
            <div className='paragraphs'>
              {listOfPlans.map((list, index) => (
                <div
                  key={index}
                  className={activeListIndex === index ? 'active' : ''}
                  onClick={() => setActiveListIndex(index)}
                >
                  <Form className='category-option' title={list.title}>
                    {list.data.length}
                  </Form>
                </div>
              ))}
            </div>
            <div className='paragraphs'>
              {securityService.checkRoleAccess(UserPermissions.isSuperUser) && (
                <div onClick={() => ShowConversionEmails()}>
                  <Form className='category-option' title='Manual Access'>
                    {state.userConversionData?.active?.manual?.length ?? '0'}
                  </Form>
                </div>
              )}
              <div>
                <Form title='Total'>
                  {state.userConversionData?.total_count ?? '0'}
                </Form>
              </div>
            </div>
          </div>
        ) : (
          <h2>No data for reporting</h2>
        )}
      </InstructionWrapper>
    </div>
  );
}
